import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell, { Avviso, TestataModulo } from "@/components/auth/AuthShell";
import { CampoPassword } from "@/components/auth/CampiAccesso";
import { cn } from "@/lib/utils";
import { Check, CircleAlert } from "lucide-react";

const MINIMO = 10;

/**
 * Il primo accesso di chi è stato invitato: sceglie la password ed entra.
 *
 * Il link dell'email (modello «Invite user» di Supabase) porta qui con
 * `?token_hash=…`. Il link standard di Supabase non serve: rimanda con la
 * sessione nel frammento `#access_token`, e il client in modalità pkce
 * (lib/supabase.js) lo scarta con «Not a valid PKCE flow url».
 *
 * Il token si spende solo al clic su «Salva ed entra», non all'apertura della
 * pagina: i filtri antivirus della posta che aprono i link in anticipo non lo
 * bruciano. E la password si imposta SOLO sulla sessione nata da questo token:
 * se nel browser c'è già qualcuno collegato (il titolare che prova l'invito),
 * verifyOtp lo sostituisce prima di updateUser.
 */
export default function BenvenutoPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tokenHash = params.get("token_hash");
  const [password, setPassword] = useState("");
  const [ripeti, setRipeti] = useState("");
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(false);
  // Token già speso da questa pagina: se updateUser fallisce (password debole)
  // si riprova sulla stessa sessione, senza chiedere un invito nuovo.
  const verificato = useRef(false);

  const salva = async (e) => {
    e.preventDefault();
    if (!tokenHash) return; // senza il link completo la pagina non ha nemmeno il modulo
    if (password !== ripeti) {
      setErrore("Le due password non sono uguali.");
      return;
    }
    setErrore(null);
    setBusy(true);
    if (!verificato.current) {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "invite" });
      if (error) {
        setBusy(false);
        setErrore(
          "Il link d'invito è scaduto o è già stato usato. Se hai già scelto la password, entra dall'accesso; altrimenti chiedi un nuovo invito.",
        );
        return;
      }
      verificato.current = true;
    }
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setErrore(
        error.code === "weak_password"
          ? `Password troppo debole: almeno ${MINIMO} caratteri, meglio una frase che ricordi.`
          : `Non riesco a salvare la password: ${error.message}`,
      );
      return;
    }
    nav("/", { replace: true }); // il cancello chiede il codice di sicurezza a chi lo deve avere
  };

  // La regola si spunta mentre si scrive (B4-benvenuto). L'unica è la lunghezza: è la sola che Supabase chiede
  // (le due password uguali le controlla il salvataggio).
  const lunga = password.length >= MINIMO;

  if (!tokenHash) {
    return (
      <AuthShell colonna="max-w-[460px]">
        <div className="flex flex-col gap-[14px]">
          <h1 className="font-display text-h1-mobile text-ink-strong sm:text-h1">Benvenuto</h1>
          <section role="alert" data-testid="benvenuto-errore" className="flex gap-3 rounded-lg bg-danger-bg p-3.5 text-danger-ink">
            <CircleAlert className="mt-0.5 size-5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span>
              <span className="block font-bold">Questo link non è completo</span>
              <span className="mt-0.5 block">Apri il link dall&#39;email d&#39;invito, così com&#39;è.</span>
            </span>
          </section>
          <p className="mt-1.5 border-t border-line-subtle pt-3.5 text-ink-2">
            Hai già una password?{" "}
            <Link to="/login" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]">Entra dall&#39;accesso</Link>
          </p>
          <Link to="/aiuto/primo-accesso" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]" target="_blank" rel="noopener noreferrer" data-testid="aiuto-link">
            Come si entra la prima volta, passo per passo
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell colonna="max-w-[460px]">
      <form data-testid="benvenuto-form" onSubmit={salva} className="flex flex-col gap-[18px]">
        <TestataModulo titolo="Benvenuto nel gestionale">
          Scegli la tua password: la userai con la tua email per entrare da qui in poi.
        </TestataModulo>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password" className="font-semibold text-ink">Password</Label>
          <CampoPassword id="password" required minLength={MINIMO} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} testId="benvenuto-password" />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ripeti" className="font-semibold text-ink">Ripeti la password</Label>
          <Input id="ripeti" type="password" required minLength={MINIMO} autoComplete="new-password" value={ripeti} onChange={(e) => setRipeti(e.target.value)} data-testid="benvenuto-ripeti" />
        </div>

        <p aria-live="polite" className={cn("flex items-center gap-2.5 tabular-nums", lunga ? "text-success-ink" : "text-ink-2")}>
          <span aria-hidden="true" className={cn("inline-flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px] text-white", lunga ? "border-success bg-success" : "border-line-input bg-surface")}>
            {lunga ? <Check className="size-3" strokeWidth={3} /> : null}
          </span>
          <span>Almeno {MINIMO} caratteri</span>
          {lunga ? null : <span aria-hidden="true">(ne hai {password.length})</span>}
          <span className="sr-only">{lunga ? ": fatto" : ": manca"}</span>
        </p>

        {errore ? <Avviso data-testid="benvenuto-errore">{errore}</Avviso> : null}

        <Button type="submit" size="xl" loading={busy} loadingText="Salvataggio…" disabled={!lunga} className="w-full max-md:h-[54px]" data-testid="benvenuto-salva">
          Salva ed entra
        </Button>

        <div className="flex flex-col gap-1 text-ink-2">
          <p>
            Hai già una password?{" "}
            <Link to="/login" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px] md:min-h-0">Entra dall&#39;accesso</Link>
          </p>
          <Link to="/aiuto/primo-accesso" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]" target="_blank" rel="noopener noreferrer" data-testid="aiuto-link">
            Come si entra la prima volta, passo per passo
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
